import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { isGoogleMapsConfigured, loadGoogleMaps } from '../lib/googleMapsLoader.js';
import { parseGooglePlace } from '../lib/parseGooglePlace.js';
import { normalizePostalCode } from '../lib/postalCode.js';
import { autocompleteRegionCodes } from '../lib/debtorCountry.js';

/**
 * Street address input with Google Places Autocomplete (New).
 * Uses our own text field + suggestion list so styling matches other form inputs.
 * Falls back to a plain input when Maps is not configured or fails to load.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  country,
  placeholder = 'Start typing an address',
  required = false,
  id,
  className = '',
  disabled = false,
}) {
  const listId = useId();
  const inputRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const onAddressSelectRef = useRef(onAddressSelect);
  const ignoreValueFetchRef = useRef(false);
  const userTypedRef = useRef(false);
  const blurCloseTimerRef = useRef(0);
  const mapsConfigured = isGoogleMapsConfigured();
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const requestSeq = useRef(0);
  const regionCodes = useMemo(() => autocompleteRegionCodes(country), [country]);
  const regionCodesKey = regionCodes.join(',');

  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  }, [onAddressSelect]);

  useEffect(() => {
    if (!mapsConfigured || disabled) {
      setReady(false);
      return undefined;
    }

    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled) return;
        if (!google?.maps?.places?.AutocompleteSuggestion) {
          setLoadError('Google Places autocomplete is unavailable for this key.');
          setReady(false);
          return;
        }
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        setReady(true);
        setLoadError('');
      })
      .catch(() => {
        if (!cancelled) {
          setReady(false);
          setLoadError(
            'Could not load Google address autocomplete. You can still type the address.'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mapsConfigured, disabled]);

  useEffect(() => {
    if (!ready || disabled) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }

    // Only fetch after the user types in this field. Loading a saved street
    // (e.g. matter detail after submit) must not open the suggestion menu.
    if (!userTypedRef.current || ignoreValueFetchRef.current) {
      setSuggestions([]);
      setOpen(false);
      setActiveIndex(-1);
      return undefined;
    }

    const query = String(value || '').trim();
    if (query.length < 2) {
      setSuggestions([]);
      setOpen(false);
      setActiveIndex(-1);
      return undefined;
    }

    let cancelled = false;
    const seq = ++requestSeq.current;
    const timer = window.setTimeout(async () => {
      try {
        const google = window.google;
        if (!google?.maps?.places?.AutocompleteSuggestion) return;

        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        }

        const { suggestions: next } =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            sessionToken: sessionTokenRef.current,
            includedRegionCodes: regionCodes,
          });

        if (
          cancelled ||
          seq !== requestSeq.current ||
          ignoreValueFetchRef.current ||
          !userTypedRef.current
        ) {
          return;
        }

        const mapped = (next || [])
          .map((item) => item.placePrediction)
          .filter(Boolean)
          .slice(0, 6);

        setSuggestions(mapped);
        setOpen(mapped.length > 0);
        setActiveIndex(mapped.length ? 0 : -1);
      } catch {
        if (!cancelled && seq === requestSeq.current) {
          setSuggestions([]);
          setOpen(false);
        }
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [value, ready, disabled, regionCodesKey, regionCodes]);

  function clearSuggestions() {
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  }

  async function selectPrediction(prediction) {
    if (!prediction?.toPlace) return;

    // Close immediately and ignore the street value write-back from parent state.
    ignoreValueFetchRef.current = true;
    userTypedRef.current = false;
    requestSeq.current += 1;
    if (blurCloseTimerRef.current) {
      window.clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = 0;
    }
    clearSuggestions();

    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'displayName'],
      });
      const parsed = parseGooglePlace(place);
      const next = {
        ...parsed,
        postalCode: normalizePostalCode(parsed.postalCode),
      };
      if (next.street) onChange?.(next.street);
      onAddressSelectRef.current?.(next);
      if (window.google?.maps?.places?.AutocompleteSessionToken) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    } catch {
      // Keep typed value if details fail; allow suggestions again on next keystroke.
      ignoreValueFetchRef.current = false;
    }
  }

  function onKeyDown(e) {
    if (!open || !suggestions.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      void selectPrediction(suggestions[activeIndex]);
      return;
    }
    if (e.key === 'Escape') {
      clearSuggestions();
    }
  }

  const hint = disabled
    ? ''
    : loadError
      ? loadError
      : ready
        ? 'Start typing to autofill city, region, postal code, and country.'
        : mapsConfigured
          ? 'Loading address autocomplete…'
          : 'Set VITE_GOOGLE_MAPS_API_KEY to enable Google address autocomplete.';

  return (
    <div className="address-autocomplete">
      <div className="address-autocomplete-field">
        <input
          ref={inputRef}
          id={id}
          className={className}
          type="text"
          value={value}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="street-address"
          spellCheck={false}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && activeIndex >= 0 ? `${listId}-opt-${activeIndex}` : undefined
          }
          onChange={(e) => {
            userTypedRef.current = true;
            ignoreValueFetchRef.current = false;
            onChange?.(e.target.value);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            blurCloseTimerRef.current = window.setTimeout(() => {
              clearSuggestions();
              blurCloseTimerRef.current = 0;
            }, 150);
          }}
          onFocus={() => {
            if (suggestions.length && userTypedRef.current && !ignoreValueFetchRef.current) {
              setOpen(true);
            }
          }}
        />
        {open && suggestions.length ? (
          <ul id={listId} className="address-autocomplete-list" role="listbox">
            {suggestions.map((prediction, index) => {
              const main =
                prediction.mainText?.text ||
                prediction.text?.text ||
                'Address';
              const secondary = prediction.secondaryText?.text || '';
              return (
                <li
                  key={`${main}-${index}`}
                  id={`${listId}-opt-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={
                    index === activeIndex
                      ? 'address-autocomplete-option is-active'
                      : 'address-autocomplete-option'
                  }
                  onMouseDown={(e) => {
                    // Prevent input blur from racing the selection.
                    e.preventDefault();
                    void selectPrediction(prediction);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className="address-autocomplete-option-main">{main}</span>
                  {secondary ? (
                    <span className="address-autocomplete-option-secondary">{secondary}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <small className="field-hint">{hint}</small>
    </div>
  );
}
